import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { Button } from "../f7";
import type { Resource } from "../hooks/useResource";
import { Notice } from "./ui";

/**
 * The documented traps, one component each.
 *
 * Each of these explains something the API reference warns implementers about.
 * Written once and reused, because repeating an explanation on twelve pages is
 * how one page eventually forgets it.
 */

export function Loading({ what = "" }: { what?: string }) {
  return <Notice>Loading{what ? ` ${what}` : ""}…</Notice>;
}

/**
 * A 404 from a tenant route.
 *
 * NOT "this merchant does not exist". Merchants resolve from a verification
 * ledger whose rows carry a 365-day TTL, so a dormant merchant is absent while
 * being perfectly alive. Blocking works by reference regardless — which is why
 * this component takes `actions`: a page that still performs the one operation
 * that works on a 404 merchant is the honest version of this state.
 */
export function NotSeenRecently({
  reference,
  actions,
}: {
  reference: string;
  actions?: ReactNode;
}) {
  return (
    <Notice tone="amber" title="Not seen recently">
      <p>
        <span className="ref">{reference}</span> is not in the verification ledger,
        whose entries expire after a year. That does <strong>not</strong> mean the
        merchant is gone — a dormant merchant looks exactly like this.
      </p>
      {actions && (
        <div className="mt-3">
          <p className="mb-2 text-xs">
            Blocking works by reference and does not need the ledger, so it still
            applies here:
          </p>
          {actions}
        </div>
      )}
    </Notice>
  );
}

/**
 * A 503. The OPPOSITE of a 404 — we could not look.
 *
 * Never renders an empty table, and always offers a retry, because an empty
 * state here would read as "there is nothing" during a transient blip.
 */
export function CouldNotLook({
  what,
  message,
  onRetry,
}: {
  what: string;
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <Notice tone="amber" title={`Could not read ${what}`}>
      <p>
        {message ? `${message} ` : ""}This is a lookup failure, not an empty result —
        nothing has been deleted or changed.
      </p>
      {onRetry && (
        <Button outline onClick={onRetry}
          className="mt-2">
          Try again
        </Button>
      )}
    </Notice>
  );
}

/** A 403. Distinct from a sign-in problem, and says what to ask for. */
export function Denied({ message }: { message: string }) {
  return (
    <Notice tone="amber" title="You do not have access to this">
      <p>{message}</p>
      <p className="mt-2 text-xs">
        <Link to="/governance/permissions" className="underline">
          See what your groups allow
        </Link>
      </p>
    </Notice>
  );
}

export function SignedOut() {
  return (
    <Notice tone="amber" title="Signed out">
      Your session ended. Reload the page to sign in again.
    </Notice>
  );
}

export function LoadFailed({ what, message }: { what: string; message: string }) {
  return (
    <Notice tone="red" title={`Could not load ${what}`}>
      {message}
    </Notice>
  );
}

/**
 * Every non-ready `Resource` kind, rendered once.
 *
 * The seven-way ladder this replaces was pasted into about thirty panels, and
 * the copies that forgot a kind rendered nothing for it — or "Loading…"
 * forever, which reads as "slow" when the truth is "failed". The switch below
 * is exhaustive, so a new kind is a compile error here rather than a blank
 * panel somewhere.
 *
 * `notFound` is what a 404 MEANS on this panel. It differs by route — a
 * dormant merchant, a document nobody has written yet, a typo'd tier — so the
 * caller says it. It is never used for `unavailable`: "we looked and there is
 * nothing" and "we could not look" stay apart here as they do in the type.
 *
 * `children` renders the data when ready; omit it to render only the
 * not-ready states and branch on `ready` yourself.
 */
export function ResourceView<T>({
  state,
  what,
  onRetry,
  notFound,
  invalid,
  children,
}: {
  state: Resource<T>;
  /** Names the thing being read, as it reads in "Could not read …". */
  what: string;
  onRetry?: () => void;
  notFound?: ReactNode;
  /** What a 422 means here — the question was refused, e.g. an unknown audit domain. */
  invalid?: (error: Error) => ReactNode;
  children?: (data: T) => ReactNode;
}) {
  switch (state.kind) {
    case "loading":
      return <Loading what={what} />;
    case "ready":
      return <>{children ? children(state.data) : null}</>;
    case "signedout":
      return <SignedOut />;
    case "denied":
      return <Denied message={state.error.message} />;
    case "unavailable":
      return <CouldNotLook what={what} message={state.error.message} onRetry={onRetry} />;
    case "notfound":
      return <>{notFound ?? <LoadFailed what={what} message="Route not found." />}</>;
    case "invalid":
      if (invalid) return <>{invalid(state.error)}</>;
      return <LoadFailed what={what} message={state.error.message} />;
    case "error":
      return <LoadFailed what={what} message={state.error.message} />;
    default: {
      const unhandled: never = state;
      return unhandled;
    }
  }
}

/**
 * A stat tile's figure for one read: `{ value, hint }`, spread onto `Stat`.
 *
 * "…" only while the read is in flight. A failed read says so in the hint
 * and shows "—" rather than "…", which an operator waits on, or 0, which
 * they act on. A ready read whose figure is absent is also "—": the response
 * did not carry it.
 *
 * `notfound` and `unavailable` are different facts, the same ones
 * `ResourceView` keeps apart. A missing route (prod mounts no `/labeeb`
 * until an ARN is published) is not a store blip, and a 422 or a 500 is
 * neither of those. One sentence for all four left the tile unexplained.
 */
export function statOf<T>(
  state: Resource<T>,
  pick: (data: T) => ReactNode | null | undefined,
): { value: ReactNode; hint?: string } {
  switch (state.kind) {
    case "loading":
      return { value: "…" };
    case "ready": {
      const v = pick(state.data);
      return v === null || v === undefined ? { value: "—", hint: "Not in the response" } : { value: v };
    }
    case "signedout":
      return { value: "—", hint: "Signed out" };
    case "denied":
      return { value: "—", hint: "Not allowed to read this" };
    case "notfound":
      return { value: "—", hint: "Route not found." };
    case "unavailable":
      return { value: "—", hint: "Could not read — lookup failure" };
    case "invalid":
      return { value: "—", hint: "The request was refused" };
    case "error":
      return { value: "—", hint: "The read failed" };
    default: {
      const unhandled: never = state;
      return unhandled;
    }
  }
}

/** A tile retries only a lookup failure. A missing route is not a blip to repeat. */
export function retryOf(kind: string, reload: () => void): (() => void) | undefined {
  return kind === "unavailable" ? reload : undefined;
}

/**
 * An empty cap map.
 *
 * `{}` means unlimited for every tool — the designed default, per
 * usage/quotas.py rule 4 — not a misconfiguration. Rendering an empty grid
 * would invite someone to "fix" a system that is working correctly.
 */
export function EmptyByDesign({ scope }: { scope: string }) {
  return (
    <Notice title="No cap rows">
      Every tool is unlimited for {scope}. An empty cap table is the designed
      default, not a misconfiguration — tools with no row are simply not metered.
    </Notice>
  );
}
